import React, { useEffect, useState } from 'react';

interface PerplexityProgressCardProps {
  query?: string;
  isComplete?: boolean;
  onComplete?: () => void;
}

const STEPS = [
  { id: 1, label: 'Connecting to sources', delay: 0 },
  { id: 2, label: 'Fetching web content', delay: 2500 },
  { id: 3, label: 'Analyzing and summarizing', delay: 6000 },
  { id: 4, label: 'Preparing results', delay: 11000 },
];

export const PerplexityProgressCard: React.FC<PerplexityProgressCardProps> = ({
  query = 'Gathering information...',
  isComplete = false,
  onComplete,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (isComplete) {
      setCurrentStepIndex(STEPS.length); // All complete
      setClosing(true);
      const timer = setTimeout(() => {
        onComplete?.();
      }, 800);
      return () => clearTimeout(timer);
    }

    const timers = STEPS.map((step, index) => {
      if (index === 0) return null; // First step is active immediately
      return setTimeout(() => {
        setCurrentStepIndex((prev) => Math.max(prev, index));
      }, step.delay);
    });

    return () => {
      timers.forEach((t) => t && clearTimeout(t));
    };
  }, [isComplete, onComplete]);

  return (
    <div className={`my-2 w-full max-w-[85%] overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-500 ${closing ? 'opacity-0 scale-95 h-0 overflow-hidden py-0 my-0 border-transparent' : 'opacity-100 scale-100'}`}>
      <div className="mb-4">
        <h3 className="text-[15px] font-semibold text-[#183b32]">Searching the web</h3>
        {query && <p className="mt-1 text-sm text-gray-500 truncate">"{query}"</p>}
      </div>

      <div className="flex flex-col gap-3">
        {STEPS.map((step, index) => {
          const isPast = index < currentStepIndex || isComplete;
          const isActive = index === currentStepIndex && !isComplete;
          const isPending = index > currentStepIndex && !isComplete;

          let icon = "radio_button_unchecked";
          let iconClass = "text-gray-300";

          if (isPast) {
            icon = "check_circle";
            iconClass = "text-[#2f4941]";
          } else if (isActive) {
            icon = "sync";
            iconClass = "text-[#2f4941] animate-spin";
          }

          return (
            <div key={step.id} className={`flex items-center gap-3 transition-opacity duration-300 ${isPending ? 'opacity-50' : 'opacity-100'}`}>
              <span className={`material-icons text-[20px] ${iconClass}`}>{icon}</span>
              <span className={`text-[14px] ${isPast || isActive ? 'font-medium text-[#183b32]' : 'text-gray-500'}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-5 border-t border-gray-50 pt-3">
        <p className="text-xs text-gray-400">This usually takes 10–30 seconds.</p>
      </div>
    </div>
  );
};
